import type * as React from "react";

/** Small entity/status label. */
export interface BadgeProps {
  children?: React.ReactNode;
  /** Entity tone — drives color. @default "neutral" */
  tone?: "person" | "company" | "event" | "opportunity" | "note" | "neutral";
  /** "soft" filled pill (default) or "dot" outlined with a leading color dot. @default "soft" */
  variant?: "soft" | "dot";
  /** @default "md" */
  size?: "sm" | "md";
  style?: React.CSSProperties;
}

interface ToneTokens {
  dot: string;
  soft: string;
  text: string;
}

const ENTITY: Record<NonNullable<BadgeProps["tone"]>, ToneTokens> = {
  person: { dot: "var(--entity-person)", soft: "var(--entity-person-soft)", text: "var(--entity-person-text)" },
  company: { dot: "var(--entity-company)", soft: "var(--entity-company-soft)", text: "var(--entity-company-text)" },
  event: { dot: "var(--entity-event)", soft: "var(--entity-event-soft)", text: "var(--entity-event-text)" },
  opportunity: { dot: "var(--entity-opportunity)", soft: "var(--entity-opportunity-soft)", text: "var(--entity-opportunity-text)" },
  note: { dot: "var(--entity-note)", soft: "var(--entity-note-soft)", text: "var(--text-secondary)" },
  neutral: { dot: "var(--warm-400)", soft: "var(--warm-100)", text: "var(--text-secondary)" },
};

const PADS = { sm: "2px 8px", md: "4px 10px" };
const FONTS = { sm: "var(--text-micro)", md: "var(--text-label)" };

export function Badge({ children, tone = "neutral", variant = "soft", size = "md", style }: BadgeProps) {
  const c = ENTITY[tone];

  if (variant === "dot") {
    return (
      <span
        style={
          {
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: PADS[size],
            borderRadius: "var(--radius-pill)",
            border: "1px solid var(--border-subtle)",
            background: "var(--surface-card)",
            fontFamily: "var(--font-sans)",
            fontSize: FONTS[size],
            fontWeight: "var(--fw-semibold)",
            color: "var(--text-body)",
            lineHeight: 1.4,
            ...style,
          } as React.CSSProperties
        }
      >
        <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: c.dot, flex: "none" }} />
        {children}
      </span>
    );
  }

  return (
    <span
      style={
        {
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          padding: PADS[size],
          borderRadius: "var(--radius-pill)",
          background: c.soft,
          color: c.text,
          fontFamily: "var(--font-sans)",
          fontSize: FONTS[size],
          fontWeight: "var(--fw-bold)",
          letterSpacing: "0.01em",
          lineHeight: 1.4,
          ...style,
        } as React.CSSProperties
      }
    >
      {children}
    </span>
  );
}
